/**
 * Admin API (§57 + additions). All routes require a valid admin session; mutating routes
 * additionally require `X-CSRF-Token`. Admin responses may contain internal IDs (needed for
 * navigation) but never secrets, code hashes/ciphertexts or plaintext codes (except the explicit,
 * audited `reveal`/`replace` actions).
 */
import { z } from 'zod';
import {
  ActivationCodeStatusSchema,
  ActorTypeSchema,
  AuditEventTypeSchema,
  BatchStatusSchema,
  DocumentStatusSchema,
  DocumentTypeSchema,
  EmailDeliveryStatusSchema,
  EntityTypeSchema,
  FindingSeveritySchema,
  IntegrationJobStatusSchema,
  IntegrationJobTypeSchema,
  ReconciliationFindingStatusSchema,
  ReconciliationFindingTypeSchema,
  RegistrationStatusSchema,
  TemplateVersionSchema,
} from '../enums';
import {
  BATCH_NAME_MAX_LENGTH,
  BATCH_QUANTITY_MAX,
  BATCH_QUANTITY_MIN,
  COMPANY_NAME_MAX_LENGTH,
  DEFAULT_TEMPLATE_VERSION,
  SLUG_MAX_LENGTH,
} from '../constants';
import {
  CodeStatsSchema,
  IdSchema,
  IsoDateTimeSchema,
  LearningSuiteGroupIdSchema,
  NullableIsoDateTimeSchema,
  PaginationQuerySchema,
  QueryBooleanSchema,
  ReasonSchema,
  SearchQuerySchema,
  SortOrderSchema,
  paginated,
  singleLineText,
} from './common';

// =============================================================================================
// Companies (§35, §36)
// =============================================================================================

export const CompanySchema = z
  .object({
    id: IdSchema,
    name: z.string(),
    slug: z.string(),
    learningsuiteGroupId: z.string(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    archivedAt: NullableIsoDateTimeSchema,
  })
  .strict();
export type Company = z.infer<typeof CompanySchema>;

export const CompanyListItemSchema = CompanySchema.extend({
  batchCount: z.number().int().nonnegative(),
  codeStats: CodeStatsSchema,
}).strict();
export type CompanyListItem = z.infer<typeof CompanyListItemSchema>;

export const CompanyDetailSchema = CompanyListItemSchema;
export type CompanyDetail = z.infer<typeof CompanyDetailSchema>;

export const CompanySortField = z.enum(['name', 'createdAt']);
export const CompanyListQuerySchema = PaginationQuerySchema.extend({
  search: SearchQuerySchema,
  includeArchived: QueryBooleanSchema.default(false),
  sort: CompanySortField.default('name'),
  order: SortOrderSchema.default('asc'),
});
export type CompanyListQuery = z.infer<typeof CompanyListQuerySchema>;
export const CompanyListResponseSchema = paginated(CompanyListItemSchema);
export type CompanyListResponse = z.infer<typeof CompanyListResponseSchema>;

const SlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(SLUG_MAX_LENGTH)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u, 'Nur Kleinbuchstaben, Ziffern und Bindestriche.');

export const CreateCompanyRequestSchema = z
  .object({
    name: singleLineText(COMPANY_NAME_MAX_LENGTH),
    learningsuiteGroupId: LearningSuiteGroupIdSchema,
    /** Optional; derived from the name via `slugify` if omitted. Must be unique. */
    slug: SlugSchema.optional(),
  })
  .strict();
export type CreateCompanyRequest = z.input<typeof CreateCompanyRequestSchema>;

/**
 * Changing `learningsuiteGroupId` only affects batches created afterwards (§8 snapshot).
 * `archived: true` archives (sets archived_at), `false` restores.
 */
export const UpdateCompanyRequestSchema = z
  .object({
    name: singleLineText(COMPANY_NAME_MAX_LENGTH).optional(),
    learningsuiteGroupId: LearningSuiteGroupIdSchema.optional(),
    slug: SlugSchema.optional(),
    archived: z.boolean().optional(),
  })
  .strict()
  .refine((v) => Object.values(v).some((field) => field !== undefined), {
    message: 'Mindestens ein Feld angeben.',
  });
export type UpdateCompanyRequest = z.input<typeof UpdateCompanyRequestSchema>;

// =============================================================================================
// Batches (§8, §37, §38, §99)
// =============================================================================================

export const BatchListItemSchema = z
  .object({
    id: IdSchema,
    companyId: IdSchema,
    /** Snapshot taken at creation (§8). */
    companyNameSnapshot: z.string(),
    /** Snapshot taken at creation (§8, §23). */
    learningsuiteGroupIdSnapshot: z.string(),
    name: z.string(),
    slug: z.string(),
    quantity: z.number().int().positive(),
    /** Codes generated so far; equals `quantity` when READY. */
    generatedCount: z.number().int().nonnegative(),
    status: BatchStatusSchema,
    templateVersion: TemplateVersionSchema,
    expiresAt: NullableIsoDateTimeSchema,
    googleSheetUrl: z.url().nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    disabledAt: NullableIsoDateTimeSchema,
    codeStats: CodeStatsSchema,
  })
  .strict();
export type BatchListItem = z.infer<typeof BatchListItemSchema>;

export const BatchSortField = z.enum(['createdAt', 'name']);
export const BatchListQuerySchema = PaginationQuerySchema.extend({
  companyId: IdSchema.optional(),
  status: BatchStatusSchema.optional(),
  search: SearchQuerySchema,
  sort: BatchSortField.default('createdAt'),
  order: SortOrderSchema.default('desc'),
});
export type BatchListQuery = z.infer<typeof BatchListQuerySchema>;
export const BatchListResponseSchema = paginated(BatchListItemSchema);
export type BatchListResponse = z.infer<typeof BatchListResponseSchema>;

/** Generated documents (PDF/ZIP/CSV) – status, download via /admin/documents/:id/download. */
export const GeneratedDocumentSchema = z
  .object({
    id: IdSchema,
    batchId: IdSchema,
    /** Set for CODE_PDF. */
    activationCodeId: IdSchema.nullable(),
    type: DocumentTypeSchema,
    status: DocumentStatusSchema,
    templateVersion: TemplateVersionSchema,
    /** Download file name without PII (§43, §160), e.g. `muster-gmbh-willkommenspaket-2026-10.pdf`. */
    fileName: z.string(),
    /** Pages of a PDF; for BATCH_CSV the number of data rows (codes) without the header. */
    pageCount: z.number().int().nonnegative().nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    /** Sanitised error text for FAILED documents. */
    error: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    generatedAt: NullableIsoDateTimeSchema,
    /** BATCH_CSV with participant names/emails (§54); always false for PDFs/ZIP. */
    includePersonalData: z.boolean(),
    /** BATCH_CSV progress (ADR 066): parts of CSV_CODES_PER_PART codes; null for PDFs/ZIP. */
    partsTotal: z.number().int().positive().nullable(),
    partsDone: z.number().int().nonnegative().nullable(),
  })
  .strict();
export type GeneratedDocument = z.infer<typeof GeneratedDocumentSchema>;

export const BatchDetailSchema = BatchListItemSchema.extend({
  companyName: z.string(),
  companyArchived: z.boolean(),
  /** Latest documents of this batch (newest first, max. 10). */
  documents: z.array(GeneratedDocumentSchema),
}).strict();
export type BatchDetail = z.infer<typeof BatchDetailSchema>;

/**
 * §38: creates the batch in status GENERATING; codes are generated asynchronously in chunks.
 * Snapshots of company name and LearningSuite group ID are taken atomically.
 */
export const CreateBatchRequestSchema = z
  .object({
    companyId: IdSchema,
    name: singleLineText(BATCH_NAME_MAX_LENGTH),
    quantity: z.number().int().min(BATCH_QUANTITY_MIN).max(BATCH_QUANTITY_MAX),
    /** Optional expiry (§8); must be in the future. */
    expiresAt: IsoDateTimeSchema.nullable().default(null),
    templateVersion: TemplateVersionSchema.default(DEFAULT_TEMPLATE_VERSION),
  })
  .strict();
export type CreateBatchRequest = z.input<typeof CreateBatchRequestSchema>;

export const DisableBatchRequestSchema = z.object({ reason: ReasonSchema.optional() }).strict();
export type DisableBatchRequest = z.infer<typeof DisableBatchRequestSchema>;
export const DisableBatchResponseSchema = z
  .object({
    batch: BatchDetailSchema,
    affected: z
      .object({
        /** AVAILABLE/PENDING_EMAIL codes set to DISABLED. */
        codesDisabled: z.number().int().nonnegative(),
        /** PENDING_EMAIL registrations set to CANCELLED. */
        registrationsCancelled: z.number().int().nonnegative(),
      })
      .strict(),
  })
  .strict();
export type DisableBatchResponse = z.infer<typeof DisableBatchResponseSchema>;

// POST /admin/batches/:id/documents → 202 GeneratedDocument (READY immediately on cache hit)
export const RequestBatchDocumentRequestSchema = z.discriminatedUnion('type', [
  z.object({ type: z.enum(['BATCH_PDF', 'BATCH_ZIP']) }).strict(),
  z
    .object({
      /** Asynchronous CSV export in parts (CR-WS3-2, ADR 066) – works for every batch size. */
      type: z.literal('BATCH_CSV'),
      /** Include participant names/emails – only when explicitly needed (§54). Default false. */
      includePersonalData: z.boolean().default(false),
    })
    .strict(),
]);
export type RequestBatchDocumentRequest = z.input<typeof RequestBatchDocumentRequestSchema>;
export const DocumentResponseSchema = z.object({ document: GeneratedDocumentSchema }).strict();
export type DocumentResponse = z.infer<typeof DocumentResponseSchema>;

// GET /admin/batches/:id/csv → text/csv (UTF-8 BOM, `;`), synchronous – only up to
// CSV_SYNC_MAX_CODES codes (10 ms CPU); larger batches use the BATCH_CSV document (ADR 066).
export const BatchCsvQuerySchema = z.object({
  /** Include participant names/emails – only when explicitly needed (§54). Default false. */
  includePersonalData: QueryBooleanSchema.default(false),
});
export type BatchCsvQuery = z.infer<typeof BatchCsvQuerySchema>;

// =============================================================================================
// Integration jobs (§27, §135)
// =============================================================================================

export const IntegrationJobSummarySchema = z
  .object({
    id: IdSchema,
    type: IntegrationJobTypeSchema,
    entityType: EntityTypeSchema,
    entityId: IdSchema,
    status: IntegrationJobStatusSchema,
    attemptCount: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    nextAttemptAt: NullableIsoDateTimeSchema,
    lastErrorCode: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();
export type IntegrationJobSummary = z.infer<typeof IntegrationJobSummarySchema>;

export const IntegrationJobDetailSchema = IntegrationJobSummarySchema.extend({
  idempotencyKey: z.string(),
  lastError: z.string().nullable(),
  dispatchedAt: NullableIsoDateTimeSchema,
  lockedAt: NullableIsoDateTimeSchema,
  completedAt: NullableIsoDateTimeSchema,
  deadAt: NullableIsoDateTimeSchema,
}).strict();
export type IntegrationJobDetail = z.infer<typeof IntegrationJobDetailSchema>;

export const JobListQuerySchema = PaginationQuerySchema.extend({
  status: IntegrationJobStatusSchema.optional(),
  type: IntegrationJobTypeSchema.optional(),
  entityId: IdSchema.optional(),
});
export type JobListQuery = z.infer<typeof JobListQuerySchema>;
export const JobListResponseSchema = paginated(IntegrationJobSummarySchema);
export type JobListResponse = z.infer<typeof JobListResponseSchema>;

export const JobResponseSchema = z.object({ job: IntegrationJobDetailSchema }).strict();
export type JobResponse = z.infer<typeof JobResponseSchema>;

// POST /admin/batches/:id/sheet-sync → 202
export const SheetSyncResponseSchema = z.object({ job: IntegrationJobSummarySchema }).strict();
export type SheetSyncResponse = z.infer<typeof SheetSyncResponseSchema>;

// =============================================================================================
// Codes (§39, §100)
// =============================================================================================

export const CodeRegistrationSummarySchema = z
  .object({
    id: IdSchema,
    status: RegistrationStatusSchema,
    /** null after PII purge. */
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    emailVerifiedAt: NullableIsoDateTimeSchema,
    emailDeliveryStatus: EmailDeliveryStatusSchema,
    learningsuiteUserId: z.string().nullable(),
  })
  .strict();
export type CodeRegistrationSummary = z.infer<typeof CodeRegistrationSummarySchema>;

export const CodeListItemSchema = z
  .object({
    id: IdSchema,
    batchId: IdSchema,
    /** `•••••-•MQDX` – full code only via the audited reveal action. */
    maskedCode: z.string(),
    status: ActivationCodeStatusSchema,
    createdAt: IsoDateTimeSchema,
    pendingAt: NullableIsoDateTimeSchema,
    claimedAt: NullableIsoDateTimeSchema,
    redeemedAt: NullableIsoDateTimeSchema,
    disabledAt: NullableIsoDateTimeSchema,
    /** Set when this code was replaced (§100). */
    replacedByCodeId: IdSchema.nullable(),
    /** Set when this code is the replacement of another code. */
    replacesCodeId: IdSchema.nullable(),
    /** Current seat-holding registration (or latest one), null if none. */
    registration: CodeRegistrationSummarySchema.nullable(),
  })
  .strict();
export type CodeListItem = z.infer<typeof CodeListItemSchema>;

export const CodeListQuerySchema = PaginationQuerySchema.extend({
  status: ActivationCodeStatusSchema.optional(),
  /** Full activation code (looked up via HMAC), participant name or email. */
  search: SearchQuerySchema,
});
export type CodeListQuery = z.infer<typeof CodeListQuerySchema>;
export const CodeListResponseSchema = paginated(CodeListItemSchema);
export type CodeListResponse = z.infer<typeof CodeListResponseSchema>;

export const CodeResponseSchema = z.object({ code: CodeListItemSchema }).strict();
export type CodeResponse = z.infer<typeof CodeResponseSchema>;

export const DisableCodeRequestSchema = z.object({ reason: ReasonSchema.optional() }).strict();
export type DisableCodeRequest = z.infer<typeof DisableCodeRequestSchema>;

/** Plaintext code – only returned by audited actions (reveal, replace). */
export const RevealedCodeSchema = z
  .object({
    id: IdSchema,
    /** `XXXXX-XXXXX` */
    code: z.string(),
    /** URL encoded in the QR code (§42). */
    activationUrl: z.url(),
  })
  .strict();
export type RevealedCode = z.infer<typeof RevealedCodeSchema>;

// POST /admin/codes/:id/reveal → 200
export const RevealCodeResponseSchema = z.object({ revealed: RevealedCodeSchema }).strict();
export type RevealCodeResponse = z.infer<typeof RevealCodeResponseSchema>;

/**
 * POST /admin/codes/:id/replace (§100): disables the old code (AVAILABLE/PENDING_EMAIL only)
 * and creates a new AVAILABLE code in the same batch; both linked, audited as CODE_REPLACED.
 */
export const ReplaceCodeRequestSchema = z.object({ reason: ReasonSchema.optional() }).strict();
export type ReplaceCodeRequest = z.infer<typeof ReplaceCodeRequestSchema>;
export const ReplaceCodeResponseSchema = z
  .object({
    replacedCode: CodeListItemSchema,
    newCode: CodeListItemSchema,
    revealed: RevealedCodeSchema,
  })
  .strict();
export type ReplaceCodeResponse = z.infer<typeof ReplaceCodeResponseSchema>;

// POST /admin/codes/:id/documents → 202 { document } (CODE_PDF, §39 „PDF nur für diesen Code“)

// =============================================================================================
// Registrations (§26, §40, §98)
// =============================================================================================

export const RegistrationListItemSchema = z
  .object({
    id: IdSchema,
    status: RegistrationStatusSchema,
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    emailDeliveryStatus: EmailDeliveryStatusSchema,
    companyId: IdSchema,
    companyName: z.string(),
    batchId: IdSchema,
    batchName: z.string(),
    activationCodeId: IdSchema,
    maskedCode: z.string(),
    provisioningAttemptCount: z.number().int().nonnegative(),
    provisioningErrorCode: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    emailVerifiedAt: NullableIsoDateTimeSchema,
  })
  .strict();
export type RegistrationListItem = z.infer<typeof RegistrationListItemSchema>;

export const RegistrationListQuerySchema = PaginationQuerySchema.extend({
  status: RegistrationStatusSchema.optional(),
  emailDeliveryStatus: EmailDeliveryStatusSchema.optional(),
  companyId: IdSchema.optional(),
  batchId: IdSchema.optional(),
  /** Name or email (substring, case-insensitive). */
  search: SearchQuerySchema,
  order: SortOrderSchema.default('desc'),
});
export type RegistrationListQuery = z.infer<typeof RegistrationListQuerySchema>;
export const RegistrationListResponseSchema = paginated(RegistrationListItemSchema);
export type RegistrationListResponse = z.infer<typeof RegistrationListResponseSchema>;

export const AuditEventSchema = z
  .object({
    id: IdSchema,
    eventType: AuditEventTypeSchema,
    actorType: ActorTypeSchema,
    actorId: z.string().nullable(),
    /** Resolved admin display name for ADMIN actors. */
    actorDisplayName: z.string().nullable(),
    entityType: EntityTypeSchema,
    entityId: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()),
    requestId: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
  })
  .strict();
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const RegistrationDetailSchema = z
  .object({
    id: IdSchema,
    status: RegistrationStatusSchema,
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    emailDeliveryStatus: EmailDeliveryStatusSchema,
    verificationExpiresAt: NullableIsoDateTimeSchema,
    emailVerifiedAt: NullableIsoDateTimeSchema,
    learningsuiteUserId: z.string().nullable(),
    learningsuiteUserCreatedAt: NullableIsoDateTimeSchema,
    learningsuiteGroupAddedAt: NullableIsoDateTimeSchema,
    provisioningStartedAt: NullableIsoDateTimeSchema,
    provisioningCompletedAt: NullableIsoDateTimeSchema,
    provisioningFailedAt: NullableIsoDateTimeSchema,
    provisioningErrorCode: z.string().nullable(),
    provisioningErrorMessage: z.string().nullable(),
    provisioningAttemptCount: z.number().int().nonnegative(),
    lastProvisioningAttemptAt: NullableIsoDateTimeSchema,
    piiPurgedAt: NullableIsoDateTimeSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    company: z.object({ id: IdSchema, name: z.string() }).strict(),
    batch: z
      .object({ id: IdSchema, name: z.string(), learningsuiteGroupIdSnapshot: z.string() })
      .strict(),
    code: z
      .object({ id: IdSchema, maskedCode: z.string(), status: ActivationCodeStatusSchema })
      .strict(),
    /** Integration jobs of this registration (newest first). */
    jobs: z.array(IntegrationJobSummarySchema),
    /** Audit timeline (oldest first). */
    timeline: z.array(AuditEventSchema),
    /** Allowed admin actions in the current state (UI enables buttons accordingly). */
    actions: z
      .object({
        canRetryProvisioning: z.boolean(),
        canResendVerification: z.boolean(),
      })
      .strict(),
  })
  .strict();
export type RegistrationDetail = z.infer<typeof RegistrationDetailSchema>;

export const RegistrationDetailResponseSchema = z
  .object({ registration: RegistrationDetailSchema })
  .strict();
export type RegistrationDetailResponse = z.infer<typeof RegistrationDetailResponseSchema>;

/** POST /admin/registrations/:id/retry → 202 (§98: new job; verification untouched). */
export const RetryProvisioningResponseSchema = z
  .object({ registration: RegistrationDetailSchema, job: IntegrationJobSummarySchema })
  .strict();
export type RetryProvisioningResponse = z.infer<typeof RetryProvisioningResponseSchema>;

/** POST /admin/registrations/:id/resend-verification → 202 (only PENDING_EMAIL). */
export const AdminResendVerificationResponseSchema = z
  .object({ registration: RegistrationDetailSchema, job: IntegrationJobSummarySchema })
  .strict();
export type AdminResendVerificationResponse = z.infer<typeof AdminResendVerificationResponseSchema>;

// =============================================================================================
// Audit (§64)
// =============================================================================================

export const AuditEventListQuerySchema = PaginationQuerySchema.extend({
  eventType: AuditEventTypeSchema.optional(),
  actorType: ActorTypeSchema.optional(),
  entityType: EntityTypeSchema.optional(),
  entityId: z.string().max(64).optional(),
  from: IsoDateTimeSchema.optional(),
  to: IsoDateTimeSchema.optional(),
});
export type AuditEventListQuery = z.infer<typeof AuditEventListQuerySchema>;
export const AuditEventListResponseSchema = paginated(AuditEventSchema);
export type AuditEventListResponse = z.infer<typeof AuditEventListResponseSchema>;

// =============================================================================================
// Reconciliation findings (§108, §144)
// =============================================================================================

export const ReconciliationFindingSchema = z
  .object({
    id: IdSchema,
    type: ReconciliationFindingTypeSchema,
    severity: FindingSeveritySchema,
    status: ReconciliationFindingStatusSchema,
    entityType: EntityTypeSchema,
    entityId: z.string().nullable(),
    /** German, human-readable description without PII. */
    message: z.string(),
    details: z.record(z.string(), z.unknown()),
    occurrenceCount: z.number().int().positive(),
    firstSeenAt: IsoDateTimeSchema,
    lastSeenAt: IsoDateTimeSchema,
    resolvedAt: NullableIsoDateTimeSchema,
  })
  .strict();
export type ReconciliationFinding = z.infer<typeof ReconciliationFindingSchema>;

// POST /admin/reconciliation-findings/:id/resolve → 200 { finding }
export const ResolveFindingRequestSchema = z.object({ note: ReasonSchema.optional() }).strict();
export type ResolveFindingRequest = z.infer<typeof ResolveFindingRequestSchema>;
export const FindingResponseSchema = z.object({ finding: ReconciliationFindingSchema }).strict();
export type FindingResponse = z.infer<typeof FindingResponseSchema>;

// =============================================================================================
// Dashboard (§34) and operations (§109, §135)
// =============================================================================================

export const DashboardResponseSchema = z
  .object({
    kpis: z
      .object({
        companies: z.number().int().nonnegative(),
        activeBatches: z.number().int().nonnegative(),
        codesTotal: z.number().int().nonnegative(),
        available: z.number().int().nonnegative(),
        pendingEmail: z.number().int().nonnegative(),
        /** CLAIMED + PROVISIONING codes. */
        provisioning: z.number().int().nonnegative(),
        /** REDEEMED codes. */
        activated: z.number().int().nonnegative(),
        /** PROVISIONING_FAILED registrations + DEAD jobs + open ERROR findings. */
        errors: z.number().int().nonnegative(),
      })
      .strict(),
    recentActivity: z.array(AuditEventSchema),
    recentProvisioningFailures: z.array(RegistrationListItemSchema),
    operations: z
      .object({
        stuckJobs: z.number().int().nonnegative(),
        deadJobs: z.number().int().nonnegative(),
        openFindings: z.number().int().nonnegative(),
        bouncedEmails: z.number().int().nonnegative(),
      })
      .strict(),
  })
  .strict();
export type DashboardResponse = z.infer<typeof DashboardResponseSchema>;

/** GET /admin/operations – each list capped at 50 entries, newest first. */
export const OperationsResponseSchema = z
  .object({
    provisioningFailures: z.array(RegistrationListItemSchema),
    stuckJobs: z.array(IntegrationJobSummarySchema),
    deadJobs: z.array(IntegrationJobSummarySchema),
    failedDocuments: z.array(GeneratedDocumentSchema),
    bouncedEmails: z.array(RegistrationListItemSchema),
    openFindings: z.array(ReconciliationFindingSchema),
  })
  .strict();
export type OperationsResponse = z.infer<typeof OperationsResponseSchema>;
